import * as React from "react";

import { Currency } from "@krea/shared/types/common";

import { SegmentedToggle } from "../SegmentedToggle";
import { InputGroup, InputGroupInput } from "./input-group";
import { useNumericInput } from "./use-numeric-input";

const CURRENCY_LOCALE: Record<Currency, string> = {
  [Currency.SEK]: "sv",
  [Currency.EUR]: "fi",
};

const CURRENCY_LABEL: Record<Currency, string> = {
  [Currency.SEK]: "kr",
  [Currency.EUR]: "€",
};

export type PercentOrMoneyMode = "percent" | "amount";

const MODES: PercentOrMoneyMode[] = ["percent", "amount"];

interface IPercentOrMoneyInputProps
  extends Omit<
    React.ComponentPropsWithoutRef<"input">,
    "onChange" | "value" | "onBlur"
  > {
  value: number | null;
  onChange: (value: number | null) => void;
  onBlur?: (value: number | null) => void;
  mode: PercentOrMoneyMode;
  onModeChange: (mode: PercentOrMoneyMode) => void;
  currency: Currency;
}

const PercentOrMoneyInput = ({
  value,
  onChange,
  onBlur,
  onFocus,
  mode,
  onModeChange,
  currency,
  disabled,
  ref,
  ...props
}: IPercentOrMoneyInputProps & {
  ref?: React.Ref<HTMLInputElement>;
}) => {
  const currencyFormatter = React.useMemo(
    () =>
      new Intl.NumberFormat(CURRENCY_LOCALE[currency], {
        minimumFractionDigits: 0,
        maximumFractionDigits: 0,
      }),
    [currency],
  );

  const moneyFormat = React.useCallback(
    (val: number) => currencyFormatter.format(val),
    [currencyFormatter],
  );

  const { displayValue, handleFocus, handleChange, handleBlur } =
    useNumericInput({
      value,
      onChange,
      onBlur,
      onFocus,
      type: mode === "percent" ? "float" : "integer",
      format: mode === "amount" ? moneyFormat : undefined,
    });

  const currencyLabel = CURRENCY_LABEL[currency];
  return (
    <InputGroup className="overflow-hidden">
      <InputGroupInput
        ref={ref}
        value={displayValue}
        onFocus={handleFocus}
        onChange={handleChange}
        onBlur={handleBlur}
        inputMode={mode === "percent" ? "decimal" : "numeric"}
        disabled={disabled}
        {...props}
      />
      <div className="flex items-center border-l border-gray-300 px-1.5">
        <SegmentedToggle
          aria-label="Value mode"
          options={MODES.map((opt) => {
            const label = opt === "percent" ? "%" : currencyLabel;
            return { value: opt, label, ariaLabel: `${label} mode` };
          })}
          value={mode}
          onValueChange={onModeChange}
          disabled={disabled}
        />
      </div>
    </InputGroup>
  );
};

PercentOrMoneyInput.displayName = "PercentOrMoneyInput";

export type { IPercentOrMoneyInputProps };
export { PercentOrMoneyInput };
